import { useParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ComponentProps, use, useMemo } from 'react'
import { GetDirectLabourDataResponse, PostDirectLabourBody } from '@/feature/financial-model/ecommerce/cogs/api/types.ts'
import { usePageContext } from '@/lib/context/page'
import { ActionablePeriodRowCell, ActionableRowFeedbackDialog, TableActionableRowProvider } from '@/components/shared/tables/actionable-row.tsx'
import { CogsDirectLabourInputCell } from '@/feature/financial-model/ecommerce/cogs/components/direct-labour-input-cell.tsx'
import { COGS_BASE_KEY, createQueryKey, TOTALS_KEY } from '@/api/query-keys.ts'
import {
  getBulkDirectLabourDataMutationArgs,
  postDirectLabourDataBulk,
  useGetDirectLabourDataSuspenseQuery
} from '@/feature/financial-model/ecommerce/cogs/api'
import { PeriodCellContent, PeriodTableCell } from '@/components/shared/tables/table-cell'
import { TableRowContext } from '@/components/shared/tables/table-context'

type DirectLabourPeriod = GetDirectLabourDataResponse['periods']

export const YearlyViewRowDataCells = ({
  periods,
  dataTarget
}: {
  periods: DirectLabourPeriod
  dataTarget: 'total_fte' | 'avg_annual_gross_salary' | 'social_contributions' | 'other_charges' | 'bonus' | 'percentage_of_direct_labour'
}) => {
  const { state } = usePageContext()
  const { type: rowType } = use(TableRowContext)
  return (
    <>
      {periods.map(period => (
        <PeriodTableCell
          key={period.id ?? period.period}
          readonly={rowType !== `totals` && period.period_type !== state.period}
          cell_period={period.period}
        >
          <PeriodCellContent>
            <CogsDirectLabourInputCell period={period} selectedKey={dataTarget} />
          </PeriodCellContent>
        </PeriodTableCell>
      ))}
    </>
  )
}
export const MonthlyViewRowDataCells = ({
  periods,

  dataTarget
}: {
  periods: DirectLabourPeriod
  dataTarget: 'total_fte' | 'avg_annual_gross_salary' | 'social_contributions' | 'other_charges' | 'bonus' | 'percentage_of_direct_labour'
}) => {
  const { state } = usePageContext()
  const { id: financialModelId } = useParams<{ id: string }>()
  const { data: departmentData } = useGetDirectLabourDataSuspenseQuery(financialModelId!, state.queryPeriod, state.view)
  const departmentId = departmentData.department_id

  if (!departmentId) throw new Error(`departmentId is required`)
  if (!financialModelId) throw new Error(`financialModelId is required`)

  const queryClient = useQueryClient()
  const currentData = periods

  const actionableContextProps = useMemo(() => {
    const { mutationFn: updatePersonnelData } = getBulkDirectLabourDataMutationArgs(financialModelId)

    return {
      dataValidationType: `numeric`,
      currentData,
      mutateDataKey: dataTarget,
      rowUpdateMutationArgs: {
        mutationKey: [`copypaste`, COGS_BASE_KEY, `direct-labour`, financialModelId, `historical`, `monthly`, dataTarget],
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: createQueryKey({
              baseKey: COGS_BASE_KEY,
              keys: [`direct-labour`, financialModelId]
            })
          })
        },
        mutationItemFn: (period, { rowType }) => {
          const objectToUpdate = {
            fiscal_month: period.fiscal_month,
            fiscal_year: period.fiscal_year,
            period_type: period.period_type,
            view: state.view,
            config: {
              ...period.config,
              [dataTarget]: {
                ...period.config[dataTarget],
                type: rowType
              }
            },
            total_fte: period.total_fte,
            avg_annual_gross_salary: period.avg_annual_gross_salary,
            social_contributions: period.social_contributions,
            other_charges: period.other_charges,
            bonus: period.bonus,
            percentage_of_direct_labour: period.percentage_of_direct_labour,
            [dataTarget]: Number(period[dataTarget])
          } satisfies PostDirectLabourBody

          return updatePersonnelData(objectToUpdate)
        },
        mutationBulkFn: async (items, { rowType }) => {
          const payloads: PostDirectLabourBody[] = items.map(item => ({
            fiscal_month: item.fiscal_month,
            fiscal_year: item.fiscal_year,
            period_type: item.period_type,
            view: state.view,
            config: {
              ...item.config,
              [dataTarget]: {
                ...item.config[dataTarget],
                type: rowType
              }
            },
            total_fte: item.total_fte,
            avg_annual_gross_salary: item.avg_annual_gross_salary,
            social_contributions: item.social_contributions,
            other_charges: item.other_charges,
            bonus: item.bonus,
            percentage_of_direct_labour: item.percentage_of_direct_labour,
            [dataTarget]: Number(item[dataTarget])
          }))

          await postDirectLabourDataBulk(financialModelId!, payloads)

          queryClient.invalidateQueries({
            queryKey: createQueryKey({
              baseKey: COGS_BASE_KEY,
              keys: [`direct-labour`, financialModelId]
            })
          })

          queryClient.resetQueries({
            queryKey: createQueryKey({
              baseKey: TOTALS_KEY,
              keys: [financialModelId, `direct-labour`]
            })
          })
        }
      }
    } as Omit<ComponentProps<typeof TableActionableRowProvider<DirectLabourPeriod[number]>>, 'children'>
  }, [currentData, dataTarget, financialModelId, queryClient, state.view])

  const { type: rowType } = use(TableRowContext)

  return (
    <TableActionableRowProvider {...actionableContextProps}>
      <ActionableRowFeedbackDialog>
        {periods.map((period, cellIndex) => (
          <ActionablePeriodRowCell
            key={period.id ?? period.period}
            cellIndex={cellIndex}
            cell_period={period.period}
            readonly={rowType !== `totals` && rowType !== `total-input` && period.period_type !== state.period}
          >
            <PeriodCellContent type={state.queryPeriod === `all` && state.period !== period.period_type ? `output` : `input`}>
              <CogsDirectLabourInputCell period={period} selectedKey={dataTarget} />
            </PeriodCellContent>
          </ActionablePeriodRowCell>
        ))}
      </ActionableRowFeedbackDialog>
    </TableActionableRowProvider>
  )
}
